'use client'

import Script from 'next/script'
import { useCookieConsent, useFirstPartyTrackingSurface } from '@/lib/privacy/use-cookie-consent'
import { CAPABILITY_PATH_PREFIXES } from '@/lib/analytics/first-party-tracking-scope'

const NR_ACCOUNT_ID = process.env.NEXT_PUBLIC_NEW_RELIC_ACCOUNT_ID
const NR_AGENT_ID = process.env.NEXT_PUBLIC_NEW_RELIC_AGENT_ID
const NR_BROWSER_LICENSE_KEY = process.env.NEXT_PUBLIC_NEW_RELIC_BROWSER_LICENSE_KEY
const NR_BEACON = process.env.NEXT_PUBLIC_NEW_RELIC_BEACON || 'bam.eu01.nr-data.net'
const APP_ROOT_DOMAIN = (process.env.NEXT_PUBLIC_APP_ROOT_DOMAIN || 'maiteams.com').toLowerCase()
// Lead-form hosts (`<sub>.leads.maiteams.com`) are rewritten server-side to
// /forms/<sub>, so the BROWSER pathname is "/" and only the hostname can
// identify them — the prefix list alone is blind to the branded case.
const LEAD_FORM_DOMAIN = (process.env.NEXT_PUBLIC_LEAD_FORM_DOMAIN || 'leads.yourdomain.com').toLowerCase()

export default function NewRelicBrowser() {
  const consent = useCookieConsent()
  const surface = useFirstPartyTrackingSurface()
  if (process.env.NODE_ENV !== 'production') return null
  if (!NR_ACCOUNT_ID || !NR_AGENT_ID || !NR_BROWSER_LICENSE_KEY) return null
  // Performance monitoring is analytics: it waits for the visitor's cookie
  // choice (lib/preferences/cookie-consent.ts). It used to load
  // `beforeInteractive`; a script that must wait for a decision cannot, so the
  // agent now starts after hydration and misses the first moments of the
  // page load's timing.
  if (!surface || !consent.permissions.analytics) return null

  // Only configure the agent on the app's own non-capability URLs. On a
  // customer white-label host or capability URL we leave NREUM unconfigured so
  // the loader has no license/app to beacon to — customer traffic and
  // credentials must not flow into MaiTeams' New Relic instrumentation (Codex).
  //
  // The LOADER is appended from inside the same qualifying branch rather than
  // emitted as its own <Script>: an unconditional loader tag reached every
  // public landing page (the root layout wraps /p/ and /lp/domain/), where the
  // LP CSP rightly blocks js-agent.newrelic.com and logged a console error on
  // every published page. Do NOT fix that by widening the CSP — the loader
  // would then actually run and beacon on customer white-label hosts, which
  // the guard above exists to prevent. /p/ and /lp/domain/ are in the
  // excluded prefixes for the same reason: a customer's landing page served
  // from the app host is still customer traffic — as are /forms/ + /embed/
  // (customer lead capture, also reachable via the lead-form HOSTNAME, which
  // the server rewrite hides from pathname checks) and the capability-token
  // surfaces (/public/preferences|unsubscribe, /share/) where the token in
  // the URL is a credential that must not land in RUM telemetry — as are the
  // legacy /unsubscribe and /email-preferences recipient links, which carry the
  // recipient's address and the org UUID in the query and RENDER (rather than
  // redirect) when the contact has no preference token. Prefixes
  // are matched with and without the trailing slash because the middleware
  // applies the LP CSP to the bare "/p" 404 too.
  const nrConfig = `
;window.NREUM||(NREUM={});
(function(){var h=(location.hostname||"").toLowerCase();var pathname=(location.pathname||"").replace(/%([0-9a-f]{2})/gi,function(m,x){return String.fromCharCode(parseInt(x,16));}).toLowerCase();var capabilityPrefixes=${JSON.stringify([...CAPABILITY_PATH_PREFIXES])};var isCapabilityPath=capabilityPrefixes.some(function(prefix){return pathname===prefix.slice(0,-1)||pathname.startsWith(prefix);});var isLeadFormHost=h==="${LEAD_FORM_DOMAIN}"||h.endsWith(".${LEAD_FORM_DOMAIN}");if(isCapabilityPath||isLeadFormHost||!(h==="${APP_ROOT_DOMAIN}"||h.endsWith(".${APP_ROOT_DOMAIN}")||h==="localhost"||h.endsWith(".localhost")||h==="127.0.0.1"||h.endsWith(".vercel.app")))return;
NREUM.init={distributed_tracing:{enabled:true},performance:{capture_measures:true},browser_consent_mode:{enabled:false},privacy:{cookies_enabled:true},ajax:{deny_list:["${NR_BEACON}"],capture_payloads:"none"}};
NREUM.loader_config={accountID:"${NR_ACCOUNT_ID}",trustKey:"${NR_ACCOUNT_ID}",agentID:"${NR_AGENT_ID}",licenseKey:"${NR_BROWSER_LICENSE_KEY}",applicationID:"${NR_AGENT_ID}"};
NREUM.info={beacon:"${NR_BEACON}",errorBeacon:"${NR_BEACON}",licenseKey:"${NR_BROWSER_LICENSE_KEY}",applicationID:"${NR_AGENT_ID}",sa:1};
var s=document.createElement("script");s.src="https://js-agent.newrelic.com/nr-loader-spa-current.min.js";s.async=false;document.head.appendChild(s);
})();
`

  return (
    <Script
      id="nr-browser-config"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: nrConfig }}
    />
  )
}
